// Chuyển từ VUONGDATWOOD_V2/src/features/cong-luong/template-resolver.ts
// Mẫu: { ID, TO_LIST, NHAN_SU_LIST, NHAN_SU_LOAI_TRU } — NHAN_SU_* chứa SỐ HĐ.

// Tên tổ do người dùng gõ nên cùng một tổ có nhiều cách viết; so thô sẽ âm thầm bỏ sót người.
export function normalizeTeamKey(value) {
    return String(value ?? '').trim().replace(/\s+/g, ' ').toLocaleLowerCase('vi');
}

export const normalizePayrollTeam = normalizeTeamKey;

// Loại trừ thắng mọi cách khớp khác: cấu hình mâu thuẫn thì nghiêng về phía không áp mẫu, người đó
// rơi xuống mẫu kế tiếp và vẫn có lương, còn áp nhầm mẫu thì ra số tiền sai mà không ai thấy.
export function isPayrollTemplateExcluded(template, employeeId) {
    return (template.NHAN_SU_LOAI_TRU ?? []).includes(employeeId);
}

export function matchesPayrollEmployee(template, employeeId) {
    if (isPayrollTemplateExcluded(template, employeeId)) return false;
    return (template.NHAN_SU_LIST ?? []).includes(employeeId);
}

/**
 * Giá trị đặc biệt trong DS TỔ: mọi người đang phụ trách công đoạn trong Phân bổ (tức có tổ), không
 * phải liệt kê từng tổ và tổ mới thêm vào cũng tự có. Tổ nêu tên cụ thể ở mẫu khác vẫn thắng.
 */
export const ALL_PRODUCTIVITY_TEAMS = '*';

const teamListOf = (template) => (template.TO_LIST ?? []).filter((item) => item !== ALL_PRODUCTIVITY_TEAMS);
const wantedTeams = (teams) => (Array.isArray(teams) ? teams : [teams]).map(normalizePayrollTeam).filter(Boolean);

// Nhận danh sách tổ vì một người có thể ở nhiều tổ. Chưa phân tổ thì không khớp mẫu theo tổ nào.
export function matchesPayrollTeam(template, teams, employeeId) {
    if (employeeId && isPayrollTemplateExcluded(template, employeeId)) return false;
    const wanted = wantedTeams(teams);
    if (!wanted.length) return false;
    return teamListOf(template).some((item) => wanted.includes(normalizePayrollTeam(item)));
}

export function appliesToAllProductivityTeams(template) {
    return (template.TO_LIST ?? []).includes(ALL_PRODUCTIVITY_TEAMS);
}

export function matchesProductivityRecipient(template, teams, employeeId) {
    if (employeeId && isPayrollTemplateExcluded(template, employeeId)) return false;
    return appliesToAllProductivityTeams(template) && wantedTeams(teams).length > 0;
}

export function isCompanyWidePayrollTemplate(template) {
    return !(template.TO_LIST ?? []).length && !(template.NHAN_SU_LIST ?? []).length;
}

// Ưu tiên: thỏa thuận > nêu đích danh nhân sự > tổ > mọi người nhận năng suất > toàn công ty.
// Nhiều mẫu cùng cấp là xung đột.
export function resolveSelectedPayrollTemplateForEmployee(input) {
    if (input.agreementTemplate) return { status: 'RESOLVED', template: input.agreementTemplate, level: 'THOA_THUAN' };
    if (input.employeeTemplates.length > 1) return { status: 'CONFLICT', templates: input.employeeTemplates, level: 'NHAN_SU' };
    if (input.employeeTemplates.length === 1) return { status: 'RESOLVED', template: input.employeeTemplates[0], level: 'NHAN_SU' };
    if (input.teamTemplates.length > 1) return { status: 'CONFLICT', templates: input.teamTemplates, level: 'TO' };
    if (input.teamTemplates.length === 1) return { status: 'RESOLVED', template: input.teamTemplates[0], level: 'TO' };
    const productivityTemplates = input.productivityTemplates ?? [];
    if (productivityTemplates.length > 1) return { status: 'CONFLICT', templates: productivityTemplates, level: 'NANG_SUAT' };
    if (productivityTemplates.length === 1) return { status: 'RESOLVED', template: productivityTemplates[0], level: 'NANG_SUAT' };
    if (input.companyTemplates.length > 1) return { status: 'CONFLICT', templates: input.companyTemplates, level: 'TOAN_CONG_TY' };
    if (input.companyTemplates.length === 1) return { status: 'RESOLVED', template: input.companyTemplates[0], level: 'TOAN_CONG_TY' };
    return { status: 'EXCLUDED' };
}

// Lý do không khớp mẫu nào, mỗi lý do sửa ở một màn khác nhau nên phải tách riêng.
export const PAYROLL_EXCLUSION_REASONS = {
    CHUA_PHAN_BO: 'Chưa được phân vào tổ nào',
    TO_KHONG_CO_MAU: 'Tổ của nhân sự không có mẫu lương',
    BI_LOAI_TRU: 'Bị loại trừ khỏi mẫu',
    THOA_THUAN_MAU_KHAC: 'Thỏa thuận lương dùng mẫu chưa được chọn cho kỳ',
};

/**
 * Người này dùng mẫu nào trong tập mẫu đang xét; không khớp thì nói luôn vì sao. Màn tạo kỳ, màn thỏa
 * thuận và bảng độ phủ cùng gọi hàm này để không phán khác nhau về cùng một người.
 *
 * input: { employeeId, teams, agreementTemplate, agreementTemplateId, templates }
 */
export function resolvePayrollTemplateForEmployee(input) {
    // Thỏa thuận thắng mọi phạm vi khác, kể cả khi mẫu của nó không được chọn cho kỳ: khi đó báo thiếu
    // mẫu để người lập chọn thêm, còn hơn để mẫu toàn công ty gom người đó vào và trả sai lương.
    if (input.agreementTemplateId && !input.agreementTemplate) {
        return { status: 'EXCLUDED', reasonCode: 'THOA_THUAN_MAU_KHAC', excludedBy: [] };
    }
    const resolution = resolveSelectedPayrollTemplateForEmployee({
        agreementTemplate: input.agreementTemplate ?? null,
        employeeTemplates: input.templates.filter((template) => matchesPayrollEmployee(template, input.employeeId)),
        teamTemplates: input.templates.filter((template) => matchesPayrollTeam(template, input.teams, input.employeeId)),
        productivityTemplates: input.templates.filter((template) => matchesProductivityRecipient(template, input.teams, input.employeeId)),
        companyTemplates: input.templates.filter(isCompanyWidePayrollTemplate),
    });
    if (resolution.status !== 'EXCLUDED') return resolution;
    const excludedBy = input.templates.filter((template) => isPayrollTemplateExcluded(template, input.employeeId));
    const reasonCode = excludedBy.length
        ? 'BI_LOAI_TRU'
        : input.teams.length
            ? 'TO_KHONG_CO_MAU'
            : 'CHUA_PHAN_BO';
    return { status: 'EXCLUDED', reasonCode, excludedBy };
}
